import React, { useState, useRef } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import { uploadCertBadge } from '../services/storageService';
import { ensureAbsoluteUrl } from '../utils/certifications';
import toast from 'react-hot-toast';

const EMPTY_CERT = { name: '', description: '', url: '', image_url: '' };

/**
 * Rich certification editor with badge upload.
 *
 * Props:
 *  - certifications: array of { name, description, url, image_url }
 *  - onChange(updatedArray): called on every add/edit/remove
 *  - disabled: boolean
 *  - userId: string — needed for uploading badge images
 */
const CertificationEditor = ({ certifications = [], onChange, disabled, userId }) => {
  const [draft, setDraft] = useState({ ...EMPTY_CERT });
  const [editingIdx, setEditingIdx] = useState(null); // null = adding new, number = editing existing
  const [expanded, setExpanded] = useState(false); // show the form
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setDraft({ ...EMPTY_CERT });
    setEditingIdx(null);
    setExpanded(false);
  };

  const handleSave = () => {
    if (!draft.name.trim()) return;
    const entry = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      url: draft.url.trim(),
      image_url: draft.image_url.trim(),
    };

    if (editingIdx !== null) {
      const updated = [...certifications];
      updated[editingIdx] = entry;
      onChange(updated);
    } else {
      onChange([...certifications, entry]);
    }
    resetForm();
  };

  const handleEdit = (idx) => {
    setDraft({ ...certifications[idx] });
    setEditingIdx(idx);
    setExpanded(true);
  };

  const handleRemove = (idx) => {
    onChange(certifications.filter((_, i) => i !== idx));
    if (editingIdx === idx) resetForm();
  };

  const handleBadgeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    setUploading(true);
    try {
      const result = await uploadCertBadge(userId, file);
      if (!result.success) throw new Error(result.error);
      setDraft(p => ({ ...p, image_url: result.url }));
    } catch (err) {
      toast.error(err?.message || 'Failed to upload badge image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeBadge = () => {
    setDraft(p => ({ ...p, image_url: '' }));
  };

  return (
    <div className="space-y-3">
      {/* Existing certifications */}
      {certifications.length > 0 && (
        <div className="space-y-2">
          {certifications.map((cert, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 p-3 bg-muted/50 border border-border rounded-xl group"
            >
              {/* Icon or image */}
              {cert.image_url ? (
                <img
                  src={cert.image_url}
                  alt={cert.name}
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-border"
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = ''); }}
                />
              ) : null}
              {!cert.image_url ? (
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="Award" size={18} className="text-primary" />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0" style={{ display: 'none' }}>
                  <Icon name="Award" size={18} className="text-primary" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{cert.name}</p>
                {cert.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{cert.description}</p>
                )}
                {cert.url && (
                  <a
                    href={ensureAbsoluteUrl(cert.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                  >
                    <Icon name="ExternalLink" size={10} />
                    View credential
                  </a>
                )}
              </div>

              {/* Actions */}
              {!disabled && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleEdit(idx)}
                    className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title="Edit"
                  >
                    <Icon name="Pencil" size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(idx)}
                    className="p-1.5 rounded-lg hover:bg-error/10 text-muted-foreground hover:text-error transition-colors"
                    title="Remove"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit form */}
      {!disabled && (
        <>
          {expanded ? (
            <div className="border border-border rounded-xl p-4 space-y-3 bg-card">
              <p className="text-sm font-semibold text-foreground">
                {editingIdx !== null ? 'Edit Certification' : 'Add Certification'}
              </p>

              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => setDraft(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. CFEI - Certified Fire & Explosion Investigator"
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft(p => ({ ...p, description: e.target.value }))}
                  rows={2}
                  placeholder="Brief description of this certification…"
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent resize-none"
                />
              </div>

              {/* URL */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Credential Link</label>
                <div className="relative">
                  <Icon name="Link" className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    type="url"
                    value={draft.url}
                    onChange={(e) => setDraft(p => ({ ...p, url: e.target.value }))}
                    placeholder="https://verify.example.com/your-cert"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 bg-transparent"
                  />
                </div>
              </div>

              {/* Badge / Logo upload */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Badge / Logo</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                  onChange={handleBadgeUpload}
                  className="hidden"
                />

                {draft.image_url ? (
                  <div className="flex items-center gap-3 p-2.5 border border-border rounded-lg bg-muted/30">
                    <img
                      src={draft.image_url}
                      alt="Badge"
                      className="w-10 h-10 rounded-lg object-cover border border-border flex-shrink-0"
                    />
                    <span className="text-xs text-muted-foreground flex-1 truncate">Badge uploaded</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="Replace"
                      >
                        <Icon name="RefreshCw" size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={removeBadge}
                        className="p-1.5 rounded-lg hover:bg-error/10 text-muted-foreground hover:text-error transition-colors"
                        title="Remove"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex flex-col items-center justify-center gap-1.5 px-3 py-4 rounded-lg border-2 border-dashed border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors disabled:opacity-50"
                  >
                    {uploading ? (
                      <>
                        <Icon name="Loader2" size={20} className="animate-spin" />
                        <span className="text-xs">Uploading…</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Upload" size={20} />
                        <span className="text-xs">Upload badge image</span>
                        <span className="text-[10px] text-muted-foreground">JPEG, PNG, WebP, GIF or SVG · Max 2 MB</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSave}
                  disabled={!draft.name.trim()}
                  iconName="Check"
                  iconPosition="left"
                >
                  {editingIdx !== null ? 'Update' : 'Add'}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setDraft({ ...EMPTY_CERT }); setEditingIdx(null); setExpanded(true); }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
            >
              <Icon name="Plus" size={16} />
              Add certification
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CertificationEditor;
